import React from 'react';
import { useDispatch } from 'react-redux';
import { SocketActions } from '../store/sockets/actions';
import { TChowChat, TDataToJoin, TMessage, TRoom, TSocketState } from '../store/sockets/types';
import { socket } from '../utils/api';
import { SocketEvents } from '../utils/SocketEvents';
import { InteractiveActions } from '../store/interactive/actions';
import {
  RatingType,
  TInteractiveTest,
  TInteractiveVote,
  TLinks,
  TTestResult,
  TVoteEnd,
  TVoteRating,
  TVoteResult,
  TVotesGroup,
} from '../store/interactive/types';
import { TypeInteractive } from '../utils/TypeInteractive';
import { UsersActions } from '../store/users/actions';
import { EIcons } from '../utils/globatTypes';
import { SOCKET_ID_NAME } from '../utils/consts';
import { TCallToAction } from '../store/callToAction/types';
import { CallToActions } from '../store/callToAction/actions';
import { PrivateChatActions } from '../store/privateChat/actions';
import { TPrivateChat, TPrivateMessage } from '../store/privateChat/types';
import { setSideBar } from '../store/sidebar/actions';

export const useSocket = () => {
  const dispatch = useDispatch();

  const getInitialData = React.useCallback(() => {
    socket.on(SocketEvents.join, (data: TChowChat) => {
      dispatch(SocketActions.setRoom(data.room));
      dispatch(SocketActions.setChat(data.chat));
      dispatch(UsersActions.setUsers(data.users));
      dispatch(CallToActions.setCallToAction(null));
    });

    socket.on(SocketEvents.pinM, (data: TChowChat) => {
      dispatch(SocketActions.setRoom(data.room));
      dispatch(SocketActions.setChat(data.chat));
      dispatch(UsersActions.setUsers(data.users));
    });

    socket.on(SocketEvents.showMessages, (data: TSocketState['data']) => {
      if (data) {
        dispatch(SocketActions.setChat(data.chat));
        dispatch(UsersActions.setUsers(data.users));
      }
    });

    socket.on(SocketEvents.newMessages, (data: TMessage) => dispatch(SocketActions.setNewMessage(data)));

    socket.on(SocketEvents.likedMessages, (data: TMessage) => dispatch(SocketActions.setUpdateMessage(data)));
    socket.on(SocketEvents.editMessage, (data: TMessage) => dispatch(SocketActions.setUpdateMessage(data)));
    socket.on(SocketEvents.deleteM, (data: TMessage) => dispatch(SocketActions.setDeleteMessage(data)));

    socket.on(SocketEvents.showChat, (data: TChowChat) => {
      dispatch(SocketActions.setRoom(data.room));
      dispatch(SocketActions.setChat(data.chat));
      dispatch(UsersActions.setUsers(data.users));
    });

    socket.on(SocketEvents.startTest, (data: TInteractiveTest) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setTest(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.test));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.startVote, (data: TInteractiveVote) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setVote(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.vote));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.endVote, (data: TVoteEnd) => {
      dispatch(InteractiveActions.setEndVote(data));
    });

    socket.on(SocketEvents.startVoteRating, (data: TVoteRating) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setVoteRating(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.voteRating));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.endVoteRating, (data: TVoteEnd) => {
      dispatch(InteractiveActions.setEndVoteRating(data));
      dispatch(InteractiveActions.setVoteRating(null));
    });

    socket.on(SocketEvents.startVotesGroup, (data: TVotesGroup) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setVotesGroup(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.votesGroup));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.endVotesGroup, () => {
      dispatch(InteractiveActions.setVotesGroup(null));
    });

    socket.on(SocketEvents.startVotesRatingGroup, (data: TVotesGroup) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setVotesRatingGroup(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.votesRatingGroup));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.endVotesRatingGroup, () => {
      dispatch(InteractiveActions.setVotesRatingGroup(null));
    });

    socket.on(SocketEvents.showTest, (data: TTestResult) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setTestResult(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.testResult));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.showVote, (data: TVoteResult) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setVoteResult(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.voteResult));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.showVoteRating, (data: TVoteRating) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setVoteRatingResult(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.voteRatingResult));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.votesRating, (data: RatingType) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setRating(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.rating));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.showLink, (data: TLinks) => {
      dispatch(InteractiveActions.clearInteractive());
      dispatch(InteractiveActions.setLink(data));
      dispatch(InteractiveActions.setInteractive(TypeInteractive.link));
      dispatch(InteractiveActions.setRedirect(EIcons.interactive));
    });

    socket.on(SocketEvents.showVideo, (data: { result: number; message: string }) => {
      dispatch(InteractiveActions.setRedirect(EIcons.video));
    });

    socket.on(SocketEvents.hideVideo, (data: TRoom) => {
      dispatch(SocketActions.setRoom(data));
    });

    socket.on(SocketEvents.callToActionClick, (data: TCallToAction) => {
      dispatch(CallToActions.setCallToAction(data));
    });

    socket.on(SocketEvents.callToActionStart, (data: TCallToAction) => {
      dispatch(CallToActions.setCallToAction(data));
    });

    socket.on(SocketEvents.callToActionEnd, () => {
      dispatch(CallToActions.setCallToAction(null));
      dispatch(SocketActions.setCallActionId(0));
      sessionStorage.removeItem('callToActionId');
    });

    socket.on(SocketEvents.callToActionReset, (data: TCallToAction) => {
      dispatch(CallToActions.setCallToAction(data));
    });

    socket.on(SocketEvents.createdPChat, (data: TPrivateChat) => {
      dispatch(PrivateChatActions.setCreatedPChat(data));
    });

    socket.on(SocketEvents.newPMess, (data: TPrivateMessage) => {
      dispatch(setSideBar(EIcons.chat));
      dispatch(PrivateChatActions.setNewPMessage(data));
      dispatch(PrivateChatActions.setCurrentUserId2(data.userId1));
    });
  }, [dispatch]);

  React.useEffect(() => {
    socket.on('connect', () => {
      console.log(socket!.id, 'connect');
      localStorage.setItem(SOCKET_ID_NAME, String(socket!.id));
    });

    getInitialData();

    socket.on('disconnect', () => {
      console.log(socket, 'disconnect');
    });
  }, [getInitialData]);

  const joinToRoom = React.useCallback((eventId: number, roomId: number) => {
    const token = localStorage.getItem('token');
    if (token) {
      const dataToJoin: TDataToJoin = {
        roomId: roomId,
        event_id: eventId,
        token: token,
      };
      socket.emit(SocketEvents.join, dataToJoin);
    }
  }, []);

  const leaveRoom = React.useCallback((eventId: number, roomId: number) => {
    const token = localStorage.getItem('token');
    if (token) {
      const dataToJoin: TDataToJoin = {
        roomId: roomId,
        event_id: eventId,
        token: token,
      };
      socket.emit(SocketEvents.leave, dataToJoin);
    }
  }, []);

  return {
    joinToRoom,
    leaveRoom,
  };
};
